import { breakpoints } from '~~/common/js/constants/screen';
import {
  getBreakpoint,
} from '~~/common/js/client/screen';
import timeouts from '~~/common/js/constants/timeouts';

export const DEFAULT_INTERVAL = timeouts.SENSITIVE;

export const recognisedBreakpoints = {
  mobileL: 'isMobileLargeUp',
  tablet: 'isTabletUp',
  desktop: 'isDesktopUp',
};

/**
 * Takes a breakpoint object as defined in common/js/constants
 * and returns a MediaQueryList which watches that breakpoint in the viewport
 * https://developer.mozilla.org/en-US/docs/Web/API/MediaQueryList
 *
 * @param {Object} breakpoint the breakpoint data
 */
const createMediaQueryList = ({ minWidth }) => {
  const list = window.matchMedia?.(`(min-width: ${minWidth}px)`);
  if (!list) {
    return null;
  }
  // fix for Safari only supporting non-standard `add[remove]Listener` methods
  list.addEventListener = list.addEventListener ??
    ((event, handler) => list.addListener(handler));
  list.removeEventListener = list.removeEventListener ??
    ((event, handler) => list.removeListener(handler));
  return list;
};

// we only have one set of MediaQueryLists per module load
let mediaQueryLists = null;
// Initialises shared MediaQueryLists (called the first time the mixin is mounted client-side)
const createMediaQueryLists = () => {
  mediaQueryLists = Object.entries(breakpoints)
    .filter(([key]) => Object.keys(recognisedBreakpoints).includes(key))
    .map(([key, breakpoint]) => {
      const mediaQueryList = createMediaQueryList(breakpoint);
      if (!mediaQueryList) {
        return null;
      }

      return {
        key,
        mediaQueryList,
      };
    })
    // get rid of breakpoints for which we couldn't create a MediaQueryList
    .filter((list) => !!list);
};

/**
 * This mixin uses the matchMedia API to update the properties
 */
export default {
  data: () => ({
    breakpoint: null,
    isMobileLargeUp: null,
    isTabletUp: null,
    isDesktopUp: null,
  }),
  mounted() {
    if (!mediaQueryLists) {
      createMediaQueryLists();
    }
    this.addMediaQueryListeners();
  },
  beforeDestroy() {
    if (this.mediaQueryListenerMap) {
      for (const [mediaQueryList, listener] of this.mediaQueryListenerMap) {
        mediaQueryList.removeEventListener('change', listener);
      }
    }
  },
  methods: {
    addMediaQueryListeners() {
      this.mediaQueryListenerMap = new Map();

      mediaQueryLists.forEach(({ key, mediaQueryList }) => {
        const listener = ({ matches }) => {
          this.breakpoint = getBreakpoint();
          this[recognisedBreakpoints[key]] = matches;
        };

        mediaQueryList.addEventListener('change', listener);
        // run the listener once with the current value
        listener({ matches: mediaQueryList.matches });
        // store the listener so it can be removed later
        this.mediaQueryListenerMap.set(mediaQueryList, listener);
      });
    },
  },
};
